import { Loader } from 'google-maps'
import MarkerClusterer from '@google/markerclustererplus'
import memoize from 'lodash/memoize'

import googleMapsStyles from './data/googleMapsStyles'
import { createPropertyMapPopupApp } from '../vue-app'

const loadGoogleApis = memoize(() => {
  return new Loader(window.googleApisKey, {
    libraries: ['places'],
  }).load()
})

const globalStyle = window.getComputedStyle(document.documentElement)
const brandColor = globalStyle.getPropertyValue('--c-brand')

const distance2d = (latLng1, latLng2) =>
  Math.sqrt((latLng1.lat - latLng2.lat) ** 2 + (latLng1.lng - latLng2.lng) ** 2)

const groupMarkersWithSameCoords = markers => {
  const earthRadiusMeters = 6370000
  const avgLatitudeOfSwitzerland = 46.8182
  const swissParallelLength =
    2 *
    Math.PI *
    earthRadiusMeters *
    Math.cos((Math.PI * avgLatitudeOfSwitzerland) / 180)
  const degreesPerMeter = 360 / swissParallelLength
  const maxGroupingDistanceMeters = 300

  const groups = []
  markers
    .filter(el => el.lat != null && el.lng != null)
    .forEach(marker => {
      const existingGroup = groups.filter(
        group =>
          distance2d(group.coordinates, marker) <
          maxGroupingDistanceMeters * degreesPerMeter
      )[0]
      if (existingGroup) {
        existingGroup.markers.push(marker)
        existingGroup.isMultiple = true
      } else {
        const newGroup = {
          coordinates: {
            lat: marker.lat,
            lng: marker.lng,
          },
          markers: [marker],
          isMultiple: false,
        }
        groups.push(newGroup)
      }
    })
  return groups
}

class GoogleMap {
  constructor(element, options = {}) {
    this.element = element
    this.options = options
    this.markers = []
    this.circles = []
    this.createMap()
    this.disableAutoFit = false
    this.disableEmittingNextChangeEvent = false

    this.minZoomCircle = this.findMinZoomCircle()
  }

  findMinZoomCircle() {
    const { dataset } = this.element
    const num = Number(dataset.minZoomCircle)
    return Number.isNaN(num) ? 12 : num
  }

  setMinZoomCircle(val) {
    this.minZoomCircle = val
    this.hideCirclesOrMarkers()
  }

  hideCirclesOrMarkers() {
    const zoom = this.map.getZoom()
    const showCircles = zoom >= this.minZoomCircle
    this.markers.forEach(marker => marker.setVisible(!showCircles))
    this.circles.forEach(circle => circle.setVisible(showCircles))
  }

  async createMap() {
    const google = await loadGoogleApis()
    const { dataset } = this.element

    this.map = new google.maps.Map(this.element, {
      zoom: Number(dataset.zoom) || 8,
      center: { lat: 46.6224744, lng: 7.2331756 },
      styles: googleMapsStyles,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
    })

    // this.oms = new window.OverlappingMarkerSpiderfier(this.map, {
    //   markersWontMove: true,
    //   markersWontHide: true,
    //   basicFormatEvents: true,
    //   // keepSpiderfied: true,
    //   circleFootSeparation: 48,
    //   spiralFootSeparation: 48,
    // })

    if ('markerClustererUrl' in dataset) {
      const maxZoom = this.minZoomCircle - 1
      this.markerClusterer = new MarkerClusterer(this.map, [], {
        imagePath: dataset.markerClustererUrl,
        maxZoom,
      })
      this.markerClusterer.getStyles().forEach(style => {
        style.fontFamily = 'inherit'
        style.fontWeight = 'normal'
        style.textSize = 12
        style.textColor = '#fff'
      })
    }

    if ('markerLat' in dataset && 'markerLng' in dataset) {
      const pos = {
        lng: Number(dataset.markerLng),
        lat: Number(dataset.markerLat),
      }
      this.setMarkers([pos])
    }

    if (this.options.initialBounds) {
      this.setMapBounds(this.options.initialBounds)
    }

    const handleChange = () => {
      if (!this.disableEmittingNextChangeEvent) {
        const bounds = this.prepareBounds(this.map.getBounds())
        const { onBoundsChange } = this.options
        if (typeof onBoundsChange === 'function') {
          onBoundsChange(bounds)
        }
      }
    }

    this.map.addListener('dragend', handleChange)
    const handleZoomChange = () => {
      this.hideCirclesOrMarkers()
      handleChange()
    }
    this.map.addListener('zoom_changed', handleZoomChange)

    this.initializePropertyPopup()
  }

  initializePropertyPopup() {
    this.infoWindowRoot = document.createElement('div')
    this.element.parentElement.appendChild(this.infoWindowRoot)
    this.infoWindowRoot.classList.add('map-popup-cards')
    this.infoWindowRoot.style.display = 'none'
    document.body.addEventListener('click', e => {
      if (!this.infoWindowRoot.contains(e.target)) {
        if (this.forceOpenPopup) {
          this.forceOpenPopup = false
        } else {
          this.infoWindowRoot.style.display = 'none'
        }
      }
    })
  }

  showPropertyPopup(selectedProperties) {
    this.forceOpenPopup = true
    this.infoWindowRoot.innerHTML = ''
    const el = document.createElement('div')
    this.infoWindowRoot.appendChild(el)
    this.infoWindowRoot.style.display = ''
    createPropertyMapPopupApp({
      el,
      data: { properties: selectedProperties },
    })
  }

  async setMarkers(markersCoords) {
    const google = await loadGoogleApis()
    const { dataset } = this.element

    if (this.markerClusterer) {
      this.markerClusterer.removeMarkers(this.markers)
    }
    this.markers.forEach(marker => marker.setMap(null))
    this.circles.forEach(circle => circle.setMap(null))
    this.markers = []
    this.circles = []

    const groups = groupMarkersWithSameCoords(markersCoords)
    groups.forEach(group => {
      const {
        coordinates: { lat, lng },
        isMultiple,
      } = group
      const circle = new google.maps.Circle({
        map: this.map,
        center: { lat, lng },
        radius: 400,
        strokeColor: brandColor,
        strokeOpacity: 1,
        strokeWeight: isMultiple ? 2 : 1,
        fillColor: brandColor,
        fillOpacity:
          0.1 + 0.9 * Math.min(Math.log(group.markers.length) / 5, 1), // between 10 and 50% depending on number of properties
      })
      this.circles.push(circle)
      // const { onMarkerClick } = this.options
      const groupGoogleMarkers = group.markers.map(data => {
        const marker = new google.maps.Marker({
          map: this.map,
          position: { lat: data.lat, lng: data.lng },
          icon: this.element.dataset.markerUrl,
        })
        if ('infoWindow' in dataset) {
          google.maps.event.addListener(marker, 'click', () => {
            this.showPropertyPopup([data], marker)
          })
        }
        return marker
      })
      if ('infoWindow' in dataset) {
        google.maps.event.addListener(circle, 'click', () => {
          this.showPropertyPopup(group.markers, groupGoogleMarkers[0])
        })
      }
      this.markers = this.markers.concat(groupGoogleMarkers)
    })

    if (this.markers.length && this.markerClusterer) {
      this.markerClusterer.addMarkers(this.markers)
    }

    if (!this.disableAutoFit) {
      this.fitToMarkers()
    }
    this.hideCirclesOrMarkers()
  }

  async fitToMarkers() {
    const google = await loadGoogleApis()

    if (this.markers.length === 0) return

    const bounds = new google.maps.LatLngBounds()
    this.markers.forEach(marker => {
      bounds.extend(marker.getPosition())
    })
    this.circles.forEach(circle => {
      bounds.union(circle.getBounds())
    })

    this.fitBoundsWhitoutEmittingEvents(bounds)
  }

  async addAutocomplete(input) {
    const google = await loadGoogleApis()
    const autocomplete = new google.maps.places.Autocomplete(input, {
      types: ['address'],
    })

    autocomplete.setComponentRestrictions({
      country: ['ch'],
    })

    autocomplete.setFields(['address_components', 'geometry', 'icon', 'name'])

    const marker = new google.maps.Marker({
      map: this.map,
      anchorPoint: new google.maps.Point(0, -29),
      icon: this.element.dataset.markerUrl,
    })

    const componentForm = {
      // administrative_area_level_1: 'short_name',
      locality: 'long_name',
      route: 'long_name',
      street_number: 'short_name',
      postal_code: 'short_name',
    }

    const setComponentValue = (component, value) => {
      const el = document.querySelector(`[data-place-data="${component}"]`)
      if (el) {
        el.value = value
        if (el !== input) {
          el.dispatchEvent(new Event('input'))
        }
      }
    }

    autocomplete.addListener('place_changed', () => {
      marker.setVisible(false)
      const place = autocomplete.getPlace()

      if (!place.geometry) {
        window.alert(`No details available for input: '${place.name}'`)
        return
      }

      if (place.geometry.viewport) {
        this.fitBoundsWhitoutEmittingEvents(place.geometry.viewport)
      } else {
        this.map.setCenter(place.geometry.location)
        this.map.setZoom(17)
      }
      marker.setPosition(place.geometry.location)
      marker.setVisible(true)

      Object.keys(componentForm).forEach(component => {
        setComponentValue(component, '')
      })

      /* eslint-disable */
      for (const component of place.address_components) {
        const addressType = component.types[0]
        if (componentForm[addressType]) {
          const val = component[componentForm[addressType]]
          setComponentValue(addressType, val)
        }
      }
      /* eslint-enable */

      const coordinates = place.geometry.location.toJSON()
      Object.keys(coordinates).forEach(key => {
        setComponentValue(key, coordinates[key])
      })

      const addressIsValid = Object.keys(componentForm).every(component => {
        const field = document.querySelector(`[data-place-data="${component}"]`)
        return field && !!field.value
      })
      const validityTracker = document.querySelector(
        '[data-autocomplete-validity-tracker]'
      )
      if (validityTracker) {
        validityTracker.checked = addressIsValid
      }
    })
  }

  prepareBounds(bounds) {
    const ne = bounds.getNorthEast().toJSON()
    const sw = bounds.getSouthWest().toJSON()
    return { north: ne.lat, east: ne.lng, west: sw.lng, south: sw.lat }
  }

  async setMapBounds({ north, west, south, east }) {
    const google = await loadGoogleApis()
    const ne = { lat: north, lng: east }
    const sw = { lat: south, lng: west }
    const bounds = new google.maps.LatLngBounds(sw, ne)
    this.fitBoundsWhitoutEmittingEvents(bounds)
  }

  async fitBoundsWhitoutEmittingEvents(bounds) {
    const google = await loadGoogleApis()
    this.disableEmittingNextChangeEvent = true
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      this.disableEmittingNextChangeEvent = false
    })
    this.map.fitBounds(bounds, 0)
  }
}

export default GoogleMap
